/**
 * HR case creation service — consent-gated Salesforce case creation for the HR RAG.
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from '../auth/msalAuth';
import { getRagByKey } from './ragConfig';

const RAG_APIM_BASE_URL = import.meta.env.VITE_APIM_BASE_URL || 'https://navigator.ai.3m.com';

/**
 * APIM publishes flat leaf routes rather than nesting under the RAG path, so this is the
 * HR endpoint suffixed rather than extended. Env override in case environments diverge.
 */
const HR_BASE_PATH = getRagByKey('HR')?.endpointPath ?? '/nav-rag-api/hr-rag';
const CREATE_CASE_PATH = import.meta.env.VITE_HR_CREATE_CASE_PATH || `${HR_BASE_PATH}-create-case`;

/**
 * Salesforce Community destinations. Overridable per environment because UAT sits on a
 * different host and path; the literals below are the production defaults.
 */
export const HR_MANUAL_CASE_URL =
  import.meta.env.VITE_HR_MANUAL_CASE_URL ||
  'https://3m3.my.site.com/globalcommunity/s/createrecord/NewHRrequest';

export const HR_COMMUNITY_URL =
  import.meta.env.VITE_HR_COMMUNITY_URL ||
  'https://3m3.my.site.com/globalcommunity/s/hrcommunitypage';

/**
 * The answer text still contains a markdown link to the manual HR Help form even when
 * one-click case creation is offered. Matching is on the URL, never the link text —
 * Ask HR replies in the user's own language so the text varies but the URL does not.
 */
const TICKET_LINK_RE =
  /\s*(?:submit\s+)?\[[^\]]*\]\((?:https?:\/\/)?3m3\.my\.site\.com\/globalcommunity\/s\/createrecord\/NewHRrequest\)/gi;

export function stripTicketLink(text: string): string {
  if (typeof text !== 'string') return text;
  return text
    .replace(TICKET_LINK_RE, '')
    .replace(/\s+([.!?])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

export interface HrCaseResult {
  caseId: string | null;
  /** May be null when the best-effort Salesforce lookup fails; the case still exists. */
  caseNumber: string | null;
  subject?: string;
  status?: string;
}

export class HrCaseError extends Error {
  /** Backend-supplied manual fallback destination, so it can change without a UI release. */
  public readonly manualCaseUrl: string;
  /** True for 400 responses, which indicate a session/auth problem rather than a backend failure. */
  public readonly isAuthError: boolean;

  constructor(message: string, manualCaseUrl: string, isAuthError = false) {
    super(message);
    this.name = 'HrCaseError';
    this.manualCaseUrl = manualCaseUrl;
    this.isAuthError = isAuthError;
  }
}

const generateRequestId = (): string => {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
        ? Array.from(crypto.getRandomValues(new Uint8Array(16)))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('')
        : `${Date.now().toString(16)}_fallback`;
  return `hr_${uuid.replace(/-/g, '')}`;
};

// 30s connect + 90s read per the backend contract; a single browser-side abort covers both.
const REQUEST_TIMEOUT_MS = 120_000;

export const HR_COMMENT_MAX_LENGTH = 200;

/**
 * Create an HR case. Never call this without explicit user consent, and never retry
 * automatically — there is no idempotency key, so every accepted request creates a real
 * Salesforce ticket that a human must triage.
 */
export async function createHrCase(
  msalInstance: IPublicClientApplication,
  chatHistory: Array<{ role: string; content: string }>,
  requestId?: string,
  comment?: string,
): Promise<HrCaseResult> {
  const endpointUrl = `${RAG_APIM_BASE_URL}${CREATE_CASE_PATH}`;

  const accessToken = await getAccessTokenHelper(msalInstance);
  const userId = msalInstance.getAllAccounts()[0]?.username || '';

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        request_id: requestId || generateRequestId(),
        timestamp: new Date().toISOString(),
        user_id: userId,
        chat_history: chatHistory,
        ...(comment?.trim() ? { comment: comment.trim().slice(0, HR_COMMENT_MAX_LENGTH) } : {}),
      }),
      signal: controller.signal,
    });
  } catch {
    // A timeout does not mean the case was not created, so the caller must not retry.
    throw new HrCaseError('We couldn\u2019t confirm your case was created.', HR_MANUAL_CASE_URL);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let manualUrl = HR_MANUAL_CASE_URL;
    let detailMessage = '';
    let backendRequestId = '';
    try {
      const body = await response.json();
      manualUrl = body?.detail?.manual_case_url || HR_MANUAL_CASE_URL;
      detailMessage = body?.detail?.error || '';
      backendRequestId = body?.detail?.request_id || '';
    } catch {
      /* non-JSON error body — fall back to defaults */
    }
    // Surfaced for support: the user only ever sees the generic fallback, but the backend
    // needs request_id to trace the failure.
    console.error('[HR case] creation failed', {
      status: response.status,
      requestId: backendRequestId,
      error: detailMessage,
    });
    // A Salesforce rejection can carry an embedded 400 without the caller's session being
    // at fault, so only treat a bare 400 (no backend detail) as an auth problem.
    const isAuthError =
      response.status === 401 || response.status === 403 || (response.status === 400 && !detailMessage);
    throw new HrCaseError(
      detailMessage || `HR case creation failed (status ${response.status}).`,
      manualUrl,
      isAuthError,
    );
  }

  const body = await response.json();
  const content = body?.data?.content ?? body?.content ?? body ?? {};
  return {
    caseId: content.case_id ?? null,
    caseNumber: content.case_number ?? null,
    subject: content.subject,
    status: content.status,
  };
}
